import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDownWideNarrow, BellRing, Loader, Map as MapaIcono, PanelTopOpen, Radio, Scan, Search, Truck, CircleAlert } from 'lucide-react';
import type { Alerta, EstadoUnidad, NivelAlerta, UnidadFlota } from '@tms/contratos';
import { ESTADOS_UNIDAD, NIVELES_ALERTA } from '@tms/contratos';
import { api } from '../api';
import { ChipEstado } from '../components/ChipEstado';
import { useAccionesAlerta } from '../components/DialogoJustificar';
import { EstadoEnVivo } from '../components/EstadoEnVivo';
import { FilaAlerta } from '../components/FilaAlerta';
import { IndicadoresFlota, type ClaveIndicador } from '../components/IndicadoresFlota';
import { Mapa, type Encuadre, type LineaMapa, type MarcadorUnidad, type VueloA } from '../components/Mapa';
import { TarjetaUnidad } from '../components/TarjetaUnidad';
import { NIVEL, UNIDAD } from '../components/estados';
import { compararPorNivel } from '../util/alertas';
import { useDatos } from '../state/Datos';
import { fmtHora, fmtKmh, fmtPct } from '../util/format';
import { useCatalogos } from '../util/catalogos';
import { useAhora, useCarga } from '../util/hooks';

type TabAlertas = 'abiertas' | 'gestionadas';

export function TorrePagina() {
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const { unidades, indicadores, alertas, cargando, error, recargar } = useDatos();
  const { resolver, pedirJustificacion, dialogo } = useAccionesAlerta();
  const ahora = useAhora(15_000);
  const catalogos = useCatalogos();

  const [busqueda, setBusqueda] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [negocio, setNegocio] = useState('');
  const [estado, setEstado] = useState<'' | EstadoUnidad>('');
  const [seleccionId, setSeleccionId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabAlertas>('abiertas');
  const [nivel, setNivel] = useState<'' | NivelAlerta>('');
  const [vuelo, setVuelo] = useState<VueloA | undefined>();
  const [claveEncuadre, setClaveEncuadre] = useState(0);

  // ---------- Filtros ----------
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLocaleLowerCase('es');
    return unidades.filter((u) => {
      const v = u.viaje;
      if (empresa && u.empresa !== empresa) return false;
      if (negocio && u.unidadNegocio !== negocio) return false;
      if (estado && u.estado !== estado) return false;
      if (!q) return true;
      const texto = [u.patente, u.interno, v?.choferPrincipal, u.vivo?.operadorNombre, v?.idMara, v?.cliente].filter(Boolean).join(' ').toLocaleLowerCase('es');
      return texto.includes(q);
    });
  }, [unidades, busqueda, empresa, negocio, estado]);

  const limpiarFiltros = useCallback(() => {
    setBusqueda('');
    setEmpresa('');
    setNegocio('');
    setEstado('');
  }, []);

  // La unidad seleccionada es la elegida si sigue visible; si no, la primera de la lista.
  const sel: UnidadFlota | undefined = filtradas.find((u) => u.vehiculoIdMicrotrack === seleccionId) ?? filtradas[0];

  // ---------- Selección y mapa ----------
  const seleccionar = useCallback((u: UnidadFlota, volar: boolean) => {
    setSeleccionId(u.vehiculoIdMicrotrack);
    if (volar && u.vivo) setVuelo((v) => ({ clave: (Number(v?.clave) || 0) + 1, lat: u.vivo!.posicion.latitud, lon: u.vivo!.posicion.longitud, zoom: 8 }));
  }, []);

  // Primer encuadre cuando llegan los datos.
  const hayDatos = unidades.length > 0;
  useEffect(() => {
    if (hayDatos) setClaveEncuadre((c) => (c === 0 ? 1 : c));
  }, [hayDatos]);

  // Deep link desde otras pantallas: /?unidad=MT-1004
  const unidadParam = params.get('unidad');
  useEffect(() => {
    if (!unidadParam || !hayDatos) return;
    const u = unidades.find((x) => x.vehiculoIdMicrotrack === unidadParam);
    if (u) {
      limpiarFiltros();
      seleccionar(u, true);
    }
    setParams({}, { replace: true });
  }, [unidadParam, hayDatos, unidades, limpiarFiltros, seleccionar, setParams]);

  const viajeId = sel?.viaje?.id ?? null;
  const enCurso = sel?.viaje?.estado === 'EN_CURSO';
  const viaje = useCarga(viajeId === null ? null : () => api.viaje(viajeId), [viajeId]);
  const recorrido = useCarga(viajeId !== null && enCurso ? () => api.recorrido(viajeId, 120) : null, [viajeId, enCurso]);

  // El recorrido real se extiende con las posiciones que llegan por el stream.
  const [extra, setExtra] = useState<[number, number][]>([]);
  useEffect(() => setExtra([]), [viajeId, recorrido.datos]);
  const lat = sel?.vivo?.posicion.latitud;
  const lon = sel?.vivo?.posicion.longitud;
  useEffect(() => {
    if (lat === undefined || lon === undefined || !enCurso) return;
    setExtra((e) => [...e.slice(-800), [lon, lat]]);
  }, [lat, lon, enCurso]);

  const lineas = useMemo<LineaMapa[]>(() => {
    const out: LineaMapa[] = [];
    if (viaje.datos && viaje.datos.id === viajeId && viaje.datos.trazaPlanificada) out.push({ id: 'planificada', variante: 'planificada', coords: viaje.datos.trazaPlanificada.coordinates });
    if (enCurso && recorrido.datos?.linea) out.push({ id: 'real', variante: 'real', coords: [...recorrido.datos.linea.coordinates, ...extra] });
    return out;
  }, [viaje.datos, viajeId, enCurso, recorrido.datos, extra]);

  const marcadores = useMemo<MarcadorUnidad[]>(
    () =>
      filtradas
        .filter((u) => u.vivo)
        .map((u) => ({
          id: u.vehiculoIdMicrotrack,
          lat: u.vivo!.posicion.latitud,
          lon: u.vivo!.posicion.longitud,
          rumbo: u.vivo!.rumbo,
          estado: u.estado,
          titulo: `${u.patente ?? u.vehiculoIdMicrotrack} · ${UNIDAD[u.estado].etiqueta} · ${u.viaje?.choferPrincipal ?? u.vivo!.operadorNombre ?? 'sin chofer'}`,
          seleccionado: u.vehiculoIdMicrotrack === sel?.vehiculoIdMicrotrack,
        })),
    [filtradas, sel?.vehiculoIdMicrotrack],
  );

  const encuadre = useMemo<Encuadre | undefined>(
    () =>
      claveEncuadre === 0
        ? undefined
        : { clave: claveEncuadre, puntos: filtradas.filter((u) => u.vivo).map((u) => [u.vivo!.posicion.longitud, u.vivo!.posicion.latitud] as [number, number]), zoomMax: 9 },
    // Se recalcula solo al pedir un encuadre (la clave), no en cada posición.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [claveEncuadre],
  );

  // ---------- Novedades ----------
  // Abiertas: las más graves primero. Gestionadas: por fecha de gestión.
  const abiertas = useMemo(() => alertas.filter((a) => a.estado === 'ABIERTA').sort(compararPorNivel), [alertas]);
  const gestionadas = useMemo(() => alertas.filter((a) => a.estado !== 'ABIERTA').sort((a, b) => (b.cambiadoEn ?? b.inicio).localeCompare(a.cambiadoEn ?? a.inicio)), [alertas]);
  const lista = (tab === 'abiertas' ? abiertas : gestionadas).filter((a) => !nivel || a.nivel === nivel);
  const porNivel = (n: NivelAlerta) => abiertas.filter((a) => a.nivel === n).length;

  const verEnMapa = (a: Alerta) => {
    limpiarFiltros();
    const u = unidades.find((x) => x.vehiculoIdMicrotrack === a.vehiculoIdMicrotrack);
    if (u) setSeleccionId(u.vehiculoIdMicrotrack);
    if (a.ubicacion) setVuelo((v) => ({ clave: (Number(v?.clave) || 0) + 1, lat: a.ubicacion!.latitud, lon: a.ubicacion!.longitud, zoom: 9 }));
  };

  const alClickIndicador = (k: ClaveIndicador) => {
    if (k === 'viajes') navegar('/viajes?estado=EN_CURSO');
    else if (k === 'alertas') {
      setTab('abiertas');
      document.getElementById('bandeja-novedades')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else setEstado((e) => (e === k ? '' : k));
  };

  const v = sel?.viaje;
  return (
    <div className="torre">
      <section className="heading">
        <div>
          <div className="eyebrow">OPERACIONES / PATAGONIA</div>
          <h1>Torre de control</h1>
        </div>
        <EstadoEnVivo />
      </section>

      <IndicadoresFlota indicadores={indicadores} activo={estado} onClick={alClickIndicador} />

      <main className="workspace">
        {/* ---------- Unidades ---------- */}
        <aside className="fleet" aria-label="Unidades">
          <div className="panel-title">
            <h2>
              Unidades <span>{filtradas.length}</span>
            </h2>
            <Truck aria-hidden />
          </div>
          <div className="filters">
            <div className="search">
              <Search aria-hidden />
              <input aria-label="Buscar unidad, chofer o viaje" placeholder="Patente, interno, chofer o viaje" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </div>
            <div className="filter-row">
              <select aria-label="Empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
                <option value="">Todas las empresas</option>
                {catalogos.empresas.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
              <select aria-label="Unidad de negocio" value={negocio} onChange={(e) => setNegocio(e.target.value)}>
                <option value="">Todos los negocios</option>
                {catalogos.unidadesNegocio.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
            <div className="filter-row">
              <select aria-label="Estado" value={estado} onChange={(e) => setEstado(e.target.value as '' | EstadoUnidad)}>
                <option value="">Todos los estados</option>
                {ESTADOS_UNIDAD.map((e) => (
                  <option key={e} value={e}>
                    {UNIDAD[e].etiqueta}
                  </option>
                ))}
              </select>
              <button type="button" className="text-button" onClick={limpiarFiltros}>
                Limpiar filtros
              </button>
            </div>
          </div>
          <div className="list-caption">
            <span>UNIDAD / VIAJE</span>
            <span>ESTADO</span>
          </div>
          <div className="unit-list">
            {cargando && (
              <div className="skeleton">
                <Loader className="spin" aria-hidden size={16} />
                Cargando unidades…
              </div>
            )}
            {!cargando && error && (
              <div className="panel-body-empty">
                <p className="red">
                  <CircleAlert aria-hidden size={14} style={{ verticalAlign: -2 }} /> {error}
                </p>
                <button type="button" className="btn-sm" style={{ marginTop: 10 }} onClick={recargar}>
                  Reintentar
                </button>
              </div>
            )}
            {!cargando && !error && filtradas.length === 0 && <p className="empty">No hay unidades con esos filtros. Probá otra búsqueda o limpiá los filtros.</p>}
            {filtradas.map((u) => (
              <TarjetaUnidad key={u.vehiculoIdMicrotrack} unidad={u} ahora={ahora} seleccionada={u.vehiculoIdMicrotrack === sel?.vehiculoIdMicrotrack} onSeleccionar={(x) => seleccionar(x, true)} />
            ))}
          </div>
          <footer className="panel-footer">
            <Radio aria-hidden />
            {api.esMock ? 'Telemetría simulada · cada 3 s' : 'Telemetría en vivo por stream'}
          </footer>
        </aside>

        {/* ---------- Mapa ---------- */}
        <section className="map-area" aria-label="Mapa de la flota">
          <Mapa
            unidades={marcadores}
            lineas={lineas}
            encuadre={encuadre}
            vueloA={vuelo}
            paddingEncuadre={{ top: 90, bottom: 200, left: 40, right: 50 }}
            onSeleccionarUnidad={(id) => setSeleccionId(id)}
          />
          <div className="map-header">
            <span>
              <MapaIcono aria-hidden />
              Corredor patagónico
            </span>
            <button type="button" className="icon" title="Centrar flota" aria-label="Centrar flota" onClick={() => setClaveEncuadre((c) => c + 1)}>
              <Scan aria-hidden />
            </button>
          </div>
          <div className="map-key">
            <span>
              <b className="route-line" />
              Recorrido real
            </span>
            <span>
              <b className="route-line planned" />
              Planificado
            </span>
          </div>
          {sel && (
            <div className="selection">
              <div className="selection-top">
                <div>
                  <Truck aria-hidden />
                  <span className="selection-title">{sel.patente ?? sel.vehiculoIdMicrotrack}</span>
                  {sel.interno && <span className="internal">#{sel.interno}</span>}
                </div>
                <ChipEstado tipo="unidad" valor={sel.estado} />
              </div>
              {v ? (
                <>
                  <div className="route-title">
                    {v.origenNombre ?? '—'} → {v.destinoNombre ?? '—'}
                  </div>
                  <div className="selection-data">
                    <span>
                      {v.idMara} · {v.choferPrincipal ?? 'Sin chofer'}
                    </span>
                    <span>
                      <b>{sel.estado === 'EN_RUTA' ? fmtKmh(sel.vivo?.velocidadKmh) : UNIDAD[sel.estado].etiqueta}</b>
                    </span>
                  </div>
                  <div className="progress" role="progressbar" aria-label="Avance del viaje" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v.avance * 100)}>
                    <span style={{ width: `${Math.round(v.avance * 100)}%` }} />
                  </div>
                  <div className="selection-data">
                    <span>{fmtPct(v.avance)} recorrido</span>
                    <span>
                      {v.proximoNombre ? `${v.proximoNombre} · ` : ''}Arribo estimado <b>{fmtHora(v.etaProximo)}</b>
                    </span>
                  </div>
                  <button type="button" onClick={() => navegar(`/viajes/${v.id}`)}>
                    <PanelTopOpen aria-hidden />
                    Ver detalle del viaje
                  </button>
                </>
              ) : (
                <p className="empty-sel">Sin viaje asignado.</p>
              )}
            </div>
          )}
          <div className="map-scale-note">
            ARGENTINA <span>·</span> UTC−3
          </div>
        </section>

        {/* ---------- Novedades ---------- */}
        <aside className="alerts" id="bandeja-novedades" aria-label="Novedades">
          <div className="panel-title">
            <h2>
              Novedades <span>{abiertas.length}</span>
            </h2>
            <BellRing aria-hidden />
          </div>
          <div className="alert-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'abiertas'} className={tab === 'abiertas' ? 'active' : ''} onClick={() => setTab('abiertas')}>
              Abiertas <span>{abiertas.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={tab === 'gestionadas'} className={tab === 'gestionadas' ? 'active' : ''} onClick={() => setTab('gestionadas')}>
              Gestionadas <span>{gestionadas.length}</span>
            </button>
          </div>
          <div className="alert-summary">
            {tab === 'abiertas'
              ? `${porNivel('CRITICA')} críticas · ${porNivel('ADVERTENCIA')} advertencias · ${porNivel('INFORMATIVA')} informativas`
              : 'Registro de gestión de las novedades'}
          </div>
          <div className="nivel-filtro" role="group" aria-label="Filtrar por nivel">
            <button type="button" className={nivel === '' ? 'active' : ''} aria-pressed={nivel === ''} onClick={() => setNivel('')}>
              Todos
            </button>
            {[...NIVELES_ALERTA].reverse().map((n) => {
              const Icono = NIVEL[n].icono;
              return (
                <button key={n} type="button" className={`${NIVEL[n].color} ${nivel === n ? 'active' : ''}`} aria-pressed={nivel === n} onClick={() => setNivel((x) => (x === n ? '' : n))}>
                  <Icono aria-hidden />
                  {NIVEL[n].etiqueta}
                </button>
              );
            })}
          </div>
          <div className="alert-list">
            {lista.length === 0 ? (
              <p className="empty">No hay novedades en esta bandeja.</p>
            ) : (
              lista.map((a) => <FilaAlerta key={a.id} alerta={a} ahora={ahora} onVerEnMapa={verEnMapa} onResolver={(x) => void resolver(x)} onJustificar={pedirJustificacion} />)
            )}
          </div>
          <footer className="panel-footer">
            <ArrowDownWideNarrow aria-hidden />
            {tab === 'abiertas' ? 'Críticas primero, después las más recientes' : 'Más recientes primero'}
          </footer>
        </aside>
      </main>
      {dialogo}
    </div>
  );
}
